import React, { createContext, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import useUserDataQuery from 'hooks/useClientUser.js';
import useUpdateClientUserMutation from 'hooks/useUpdateClientUserMutation.js';

const clientUserInitialValues = {
  displayName: '',
  fullName: '',
  email: '',
  locale: '',
};

export const ClientUserContext = createContext({ userData: clientUserInitialValues, loading: false });

export const ClientUserContextProvider = ({ children }) => {
  const { clientId } = useProductDataOutsideRoutes();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  const queryClient = useQueryClient();
  const { data: userData = clientUserInitialValues, isLoading } = useUserDataQuery(clientId, uid);
  const updateUserMutation = useUpdateClientUserMutation();

  const refreshUserData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [`/clients/${clientId}/users/${uid}`],
    });
  }, [clientId, queryClient, uid]);

  const update = useCallback(
    async ({ fullName, displayName, locale }, { catchCallback, finallyCallback } = {}) => {
      const patch = {};
      if (displayName !== undefined) patch.displayName = displayName;
      if (fullName !== undefined) patch.fullName = fullName;
      if (locale !== undefined) patch.locale = locale;
      const didUpdate = Object.keys(patch).some((key) => patch[key] !== userData[key]);
      if (!didUpdate) return;
      updateUserMutation.mutate({
        clientId,
        userId: uid,
        data: patch,
      }, { onError: catchCallback, onSettled: finallyCallback });
    },
    [clientId, uid, userData, updateUserMutation],
  );

  const context = {
    userData,
    loading: isLoading,
    updateUserData: update,
    refreshUserData,
    isSignedIn: !!uid,
    isUpdating: updateUserMutation.isLoading,
    supportedFields: Object.keys(clientUserInitialValues),
  };

  return (
    <ClientUserContext.Provider value={context}>
      {children}
    </ClientUserContext.Provider>
  );
};

export const useClientUser = () => useContext(ClientUserContext);
